/**
 * The personal GDV: a value beside the system estimate with a pencil that
 * opens a small editor (`PATCH personal-settings { gdvOverridePence,
 * expectedGdvRevision }`). The save is synchronous on the server; a stale
 * revision is reported, never retried.
 */
import { useEffect, useState } from 'react'
import { IconPencil } from '@tabler/icons-react'
import { ApiError } from '../../../api/http'
import { money } from '../../../api/format'
import { usePatchPersonalGdv, usePersonalAccess, usePersonalSettings } from '../../../api/financial'
import { poundsToPence } from '../../../api/refurbishment'
import type { PersonalSettings } from '../../../api/types'
import { Alert, Button, Field, IconButton, NumberInput, Popover } from '../../../ui/kit'
import { gdvOutputPence, isPersonalGdvOutput } from './format'

export interface GdvEditingContext { propertyId: string; snapshot: PersonalSettings | null | undefined; systemGdvPence: number | null | undefined; historical?: boolean }

const exactPounds = (pence: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(pence / 100)

/** A model output value; the raw GDV passthrough row of a built-in model carries the personal editor. */
export function FinancialOutputValue({ modelId, outputId, value, rawValue, gdvEditor }: { modelId: string; outputId: string; value: string; rawValue?: unknown; gdvEditor?: GdvEditingContext }) {
  return gdvEditor && isPersonalGdvOutput(modelId, outputId) ? <PersonalGdvValue {...gdvEditor} displayedValue={value} renderedGdvPence={gdvOutputPence(rawValue)} /> : <>{value}</>
}

export function PersonalGdvValue({ propertyId, systemGdvPence, snapshot, displayedValue, renderedGdvPence, historical = false, compact = false }: {
  propertyId: string; systemGdvPence: number | null | undefined; snapshot?: PersonalSettings | null; displayedValue?: string; renderedGdvPence?: number | null; historical?: boolean; compact?: boolean
}) {
  // Missing or denied snapshots stay inert.
  return snapshot ? <ConnectedGdvValue propertyId={propertyId} systemGdvPence={systemGdvPence} snapshot={snapshot} displayedValue={displayedValue} renderedGdvPence={renderedGdvPence} historical={historical} compact={compact} /> : <>{displayedValue ?? money(systemGdvPence)}</>
}

function ConnectedGdvValue({ propertyId, systemGdvPence, snapshot, displayedValue, renderedGdvPence, historical, compact }: {
  propertyId: string; systemGdvPence: number | null | undefined; snapshot: PersonalSettings; displayedValue?: string; renderedGdvPence?: number | null; historical: boolean; compact: boolean
}) {
  const access = usePersonalAccess()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const query = usePersonalSettings(propertyId, snapshot, open && access.read && access.valuation)
  const save = usePatchPersonalGdv(propertyId)
  const { reset } = save
  useEffect(() => { setOpen(false); setDraft(''); reset() }, [propertyId, reset])
  const current = query.data ?? snapshot
  const ownValue = access.read && access.valuation ? current.gdvOverridePence : null
  const parsed = poundsToPence(draft)
  const valid = parsed !== null && parsed > 0
  const canEdit = access.read && access.write && access.valuation
  const showEditor = () => {
    save.reset()
    const amount = ownValue ?? systemGdvPence
    setDraft(amount == null ? '' : `${Math.floor(amount / 100)}.${String(amount % 100).padStart(2, '0')}`)
    setOpen(true)
  }
  const write = async (gdvOverridePence: number | null) => {
    if (!canEdit || save.isPending) return
    try { await save.mutateAsync({ gdvOverridePence, expectedGdvRevision: current.gdvRevision }); setOpen(false) } catch { /* the error stays beside the draft */ }
  }
  return <span className="fin-gdv" data-personal={ownValue !== null || undefined} data-historical={historical || undefined}>
    <span className="fin-gdv-v">{displayedValue ?? (ownValue !== null ? exactPounds(ownValue) : money(systemGdvPence))}</span>
    {!historical && ownValue !== null && !compact && (displayedValue === undefined || renderedGdvPence === ownValue) ? <span className="fin-gdv-own">Your value</span> : null}
    {canEdit ? <Popover open={open} onClose={() => setOpen(false)} align="end" width={310} anchor={
      <IconButton small label="Edit your GDV" expanded={open} onClick={() => (open ? setOpen(false) : showEditor())}><IconPencil className="ic" aria-hidden="true" /></IconButton>
    }>
      <form className="fin-gdv-form" aria-label="Your GDV" onSubmit={event => { event.preventDefault(); if (valid) void write(parsed) }}>
        <div className="pop-title">Your GDV</div>
        <p className="t-muted">Applies to your calculations for this property.</p>
        <p>System estimate: {money(systemGdvPence)}</p>
        {ownValue !== null ? <p>Your saved GDV: {exactPounds(ownValue)}</p> : null}
        <Field label="GDV (£)" error={!valid ? 'Enter a positive amount with at most two decimal places.' : undefined}>
          <NumberInput value={draft} onChange={value => { setDraft(value); save.reset() }} prefix="£" ariaLabel="Your GDV in pounds" invalid={!valid} />
        </Field>
        {query.isError ? <Alert tight title="Current settings unavailable">{query.error.message}</Alert> : null}
        {save.isError ? <Alert tight title={save.error instanceof ApiError && save.error.status === 409 ? 'Your GDV changed elsewhere' : 'GDV could not be saved'}>{save.error.message}</Alert> : null}
        <div className="fin-gdv-acts">
          <Button small disabled={ownValue === null || save.isPending || query.isError} onClick={() => void write(null)}>Use system estimate</Button>
          <Button small kind="primary" type="submit" busy={save.isPending} disabled={!valid || query.isError || query.isFetching}>Save</Button>
        </div>
      </form>
    </Popover> : null}
  </span>
}
